import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { StaffAdmin, PlatformRole } from '../../types';
import { Modal } from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { ShieldCheck, UserPlus, Lock, Trash2, RefreshCw, Info, ShieldAlert } from 'lucide-react';

// The real platform roles (auth-service). Ordered most- to least-privileged.
const ROLE_INFO: { role: PlatformRole; label: string; desc: string }[] = [
  { role: 'super_admin', label: 'Super admin', desc: 'Full control, including managing other admins. Fixed — cannot be granted or changed here.' },
  { role: 'admin', label: 'Admin', desc: 'Business & operations: billing, subscriptions, customers, cloud, plus all technical tools. Cannot manage admins.' },
  { role: 'operator', label: 'Operator (technician)', desc: 'Technical/fleet only: cameras, camera map, offline triage, cloud storage, SIM, support tickets. No billing, customer accounts, revenue, or admin management.' },
];

// Roles that can actually be granted / changed through the console. super_admin is
// a fixed founding role set out-of-band and is deliberately excluded.
const ASSIGNABLE_ROLES = ROLE_INFO.filter((r) => r.role !== 'super_admin');

const ROLE_STYLE: Record<PlatformRole, { bg: string; color: string }> = {
  super_admin: { bg: 'rgba(168,85,247,0.14)', color: '#c084fc' },
  admin: { bg: 'rgba(56,189,248,0.14)', color: 'var(--primary-light)' },
  operator: { bg: 'rgba(148,163,184,0.14)', color: 'var(--text-muted)' },
};

export const AdminsPage: React.FC = () => {
  const { role: activeRole } = useAuth();
  const canManage = activeRole === 'super_admin';

  const [admins, setAdmins] = useState<StaffAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addMode, setAddMode] = useState<'create' | 'promote'>('create');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<PlatformRole>('operator');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAdmins(await api.getAdmins());
    } catch (err) {
      console.error('Failed to load admins', err);
      setError('Could not load platform staff. Check that you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 4000);
  };

  const errMsg = (err: unknown, fallback: string) =>
    (err as { message?: string })?.message || fallback;

  const resetAddForm = () => {
    setNewName('');
    setNewEmail('');
    setNewPassword('');
    setNewRole('operator');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (addMode === 'create') {
        if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); setBusy(false); return; }
        await api.createStaffAccount(newName.trim(), newEmail.trim().toLowerCase(), newPassword, newRole);
        flash(`Staff account created for ${newEmail.trim().toLowerCase()}.`);
      } else {
        await api.createAdmin(newEmail.trim().toLowerCase(), newRole);
        flash('Access granted.');
      }
      setIsAddOpen(false);
      resetAddForm();
      load();
    } catch (err) {
      setError(errMsg(err, addMode === 'create' ? 'Could not create the account.' : 'Could not grant access. The account must already exist.'));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (a: StaffAdmin, role: PlatformRole) => {
    if (role === a.role) return;
    setError(null);
    try {
      await api.updateAdminRole(a.id, role);
      flash(`${a.email} is now ${role.replace('_', ' ')}.`);
      load();
    } catch (err) {
      setError(errMsg(err, 'Could not change role.'));
    }
  };

  const revoke = async (a: StaffAdmin) => {
    if (!window.confirm(`Revoke admin access for ${a.email}? They revert to a regular account.`)) return;
    setError(null);
    try {
      await api.revokeAdmin(a.id);
      flash(`Revoked admin access for ${a.email}.`);
      load();
    } catch (err) {
      setError(errMsg(err, 'Could not revoke access.'));
    }
  };

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)', padding: '16px 24px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <ShieldCheck size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Admins &amp; Roles</h2>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            Who can sign in to this console, and at what level. Only a super admin can change staff.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={load}
            disabled={loading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
              padding: '6px 12px', borderRadius: '6px', cursor: loading ? 'default' : 'pointer',
              border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)',
            }}
          >
            <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => { resetAddForm(); setAddMode('create'); setIsAddOpen(true); }} disabled={!canManage}>
            {canManage ? <UserPlus size={14} /> : <Lock size={14} />}
            <span>Add staff</span>
          </button>
        </div>
      </div>

      {!canManage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(148,163,184,0.10)', border: '1px solid var(--border-dim)', color: 'var(--text-muted)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '12.5px' }}>
          <Info size={15} /> You can view staff, but only a super admin can grant, change, or revoke access.
        </div>
      )}
      {notice && (
        <div style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)', color: 'var(--success-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{notice}</div>
      )}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: '20px' }}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Staff directory</h3>
            <span className="card-subtitle">{admins.length} staff account{admins.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div style={{ padding: '20px', color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</div>
          ) : admins.length === 0 ? (
            <div style={{ padding: '20px', color: 'var(--text-dim)', fontSize: '13px' }}>No staff accounts.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {admins.map((a) => {
                const s = ROLE_STYLE[a.role] ?? ROLE_STYLE.operator;
                return (
                  <div
                    key={a.id}
                    style={{
                      background: 'rgba(15, 23, 42, 0.6)', border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)', padding: '12px 16px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong style={{ fontSize: '13.5px' }}>{a.name}</strong>
                        <span style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: s.color, background: s.bg, padding: '1px 7px', borderRadius: '4px' }}>
                          {a.role.replace('_', ' ')}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{a.email}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '2px' }}>
                        MFA {a.mfaEnabled ? 'on' : 'off'} · {a.isActive ? 'active' : 'inactive'}
                        {a.lastLogin ? ` · last login ${new Date(a.lastLogin).toLocaleDateString()}` : ' · never signed in'}
                      </div>
                    </div>

                    {canManage && (
                      a.role === 'super_admin' ? (
                        <span
                          title="The super admin account is fixed and cannot be changed here"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: 'var(--text-dim)' }}
                        >
                          <ShieldAlert size={13} /> Protected
                        </span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <select
                            className="select"
                            value={a.role}
                            onChange={(e) => changeRole(a, e.target.value as PlatformRole)}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            {ASSIGNABLE_ROLES.map((ri) => (
                              <option key={ri.role} value={ri.role}>{ri.label}</option>
                            ))}
                          </select>
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Revoke admin access"
                            onClick={() => revoke(a)}
                            style={{ color: 'var(--danger-text)' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">What each role means</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {ROLE_INFO.map((ri) => (
              <div key={ri.role} style={{ display: 'flex', gap: '10px' }}>
                <span style={{ flexShrink: 0, fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: ROLE_STYLE[ri.role].color, background: ROLE_STYLE[ri.role].bg, padding: '2px 8px', borderRadius: '4px', height: 'fit-content' }}>
                  {ri.role.replace('_', ' ')}
                </span>
                <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{ri.desc}</span>
              </div>
            ))}
            <div style={{ fontSize: '11.5px', color: 'var(--text-dim)', marginTop: '4px', borderTop: '1px solid var(--border-subtle)', paddingTop: '10px' }}>
              <strong>Add staff</strong> creates a brand-new login (name, email, password) you hand to the person — best for technicians who don't already use MyWatchman. Or switch to <strong>Promote existing</strong> to grant a role to someone who already has an account.
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add staff"
        subtitle="Create a new login, or promote an existing account"
      >
        {/* Mode toggle */}
        <div style={{ display: 'flex', border: '1px solid var(--border-dim)', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
          {(['create', 'promote'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => { setAddMode(m); setError(null); }}
              style={{ flex: 1, fontSize: '12.5px', padding: '8px 10px', cursor: 'pointer', border: 'none', borderLeft: m === 'promote' ? '1px solid var(--border-dim)' : 'none', background: addMode === m ? 'rgba(56,189,248,0.15)' : 'transparent', color: addMode === m ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: addMode === m ? 600 : 400 }}
            >
              {m === 'create' ? 'Create new account' : 'Promote existing'}
            </button>
          ))}
        </div>

        <form onSubmit={handleCreate}>
          {addMode === 'create' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Full name</label>
              <input type="text" className="input" style={{ width: '100%' }} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Jane Technician" required />
            </div>
          )}

          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
              {addMode === 'create' ? 'Email (their login)' : 'Account email'}
            </label>
            <input type="email" className="input" style={{ width: '100%' }} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="person@example.com" required />
            {addMode === 'promote' && (
              <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>Must already have a MyWatchman account.</div>
            )}
          </div>

          {addMode === 'create' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Temporary password</label>
              <input type="text" className="input" style={{ width: '100%', fontFamily: 'monospace' }} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="at least 8 characters" required minLength={8} />
              <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>Share it with the person — they can change it on the Security page after signing in.</div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Role</label>
            <select className="select" style={{ width: '100%' }} value={newRole} onChange={(e) => setNewRole(e.target.value as PlatformRole)}>
              {ASSIGNABLE_ROLES.map((ri) => (
                <option key={ri.role} value={ri.role}>{ri.label} — {ri.desc}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsAddOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              <UserPlus size={14} />
              <span>{busy ? 'Saving…' : addMode === 'create' ? 'Create account' : 'Grant access'}</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
